import { useQuery } from '@tanstack/react-query'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { shipmentPlanConverter, repairTruckConverter, transportTruckConverter } from '@otw/models/converters'
import type { RepairTruck, ShipmentPlan, TransportTruck } from '@otw/models/logistics'

export type TimelineShipmentRepairData = {
  trucks: TransportTruck[]
  plans: ShipmentPlan[]
  repairTrucks: RepairTruck[]
}

export const timelineShipmentRepairQueryKey = (vehicleId?: string) =>
  ['timeline_shipment_repair', vehicleId] as const

async function fetchTimelineShipmentRepair(vehicleId: string): Promise<TimelineShipmentRepairData> {
  const trucksCollection = collection(db, 'transport_trucks').withConverter(transportTruckConverter)
  const plansCollection = collection(db, 'shipment_plans').withConverter(shipmentPlanConverter)
  const repairCollection = collection(db, 'repair_trucks').withConverter(repairTruckConverter)

  const trucksSnap = await getDocs(query(trucksCollection, where('vehicle_id', '==', vehicleId)))
  const trucks = trucksSnap.docs.map((d) => d.data())
  const truckIds = trucks.map((t) => t.id)
  if (truckIds.length === 0) return { trucks: [], plans: [], repairTrucks: [] }

  const [planSnaps, repairSnaps] = await Promise.all([
    Promise.all(
      truckIds.map((truckId) =>
        getDocs(query(plansCollection, where('transport_truck_id', '==', truckId)))
      )
    ),
    Promise.all(
      truckIds.map((truckId) =>
        getDocs(query(repairCollection, where('transport_truck_id', '==', truckId)))
      )
    ),
  ])

  return {
    trucks,
    plans: planSnaps.flatMap((snap) => snap.docs.map((d) => d.data())),
    repairTrucks: repairSnaps.flatMap((snap) => snap.docs.map((d) => d.data())),
  }
}

export function useTimelineShipmentRepair(
  vehicleId: string | undefined,
  options?: { enabled?: boolean }
) {
  const enabled = (options?.enabled ?? true) && Boolean(vehicleId)

  return useQuery({
    queryKey: timelineShipmentRepairQueryKey(vehicleId),
    enabled,
    queryFn: async () => {
      if (!vehicleId) return { trucks: [], plans: [], repairTrucks: [] }
      return fetchTimelineShipmentRepair(vehicleId)
    },
  })
}
